"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const pino_1 = __importDefault(require("pino"));
/*
 * TODO: express-pino-logger actually links to pino-http
 * https://github.com/pinojs/express-pino-logger/blob/master/logger.js#L2
 * So we should just go straight to the source
 */
const express_pino_logger_1 = __importDefault(require("express-pino-logger"));
const merge_1 = __importDefault(require("lodash/merge"));
const union_1 = __importDefault(require("lodash/union"));
const config_1 = __importDefault(require("./config"));
let config;
// REDACTION
/**
 * When adjusting this list you must also update the expected test results;
 * packages/logger/__tests__/__snapshots__/logger.unit.test.ts.snap
 */
const defaultUserPiiProps = [
    "dob",
    "email",
    "emailAddress",
    "firstName",
    "gender",
    "lat",
    "lastName",
    "long",
    "parentalUnitName",
    "pb_long",
    "phoneNumber",
    "preferredName",
    "username"
];
/**
 * a list of known paths through nested objects that
 * contain user PII properties.
 */
const userPiiPropPaths = [
    "data.location",
    "dtcRequestor",
    "err.data",
    "eventMessage.headers.metainfo.dtcFamily.usersList[*]",
    "eventMessage.headers.metainfo.dtcRequestor",
    "response.family.usersList[*]",
    "response.requestor",
    "variables.location"
];
function buildUserPiiRedactList(userPiiPaths, userPiiProps) {
    // Array.prototype.flatMap is only supported in node v11.0.0,
    // but some Greenlight services are running on lower node versions.
    return userPiiPaths.reduce((redactList, path) => redactList.concat(userPiiProps.map(prop => `${path}.${prop}`)), []);
}
const userPiiRedactList = buildUserPiiRedactList(userPiiPropPaths, defaultUserPiiProps);
/**
 * Paths from object to redact
 * NOTE: If in the future we want to remove the keys then
 * modify redact as follows
 * redact = {
 *    paths: [] --> current array
 *    remove: true
 * }
 * @see http://getpino.io/#/docs/redaction
 */
const redact = [
    "req.headers.authorization",
    "ssn",
    "password",
    "balance",
    "new_password",
    "confirm_password",
    "verify_password",
    "current_password",
    "token",
    "braintree_token",
    "account_number",
    "routing_number",
    "accountNumber",
    "routingNumber",
    "signed_request",
    "access_token",
    "plaid_access_key",
    "username",
    "ssnLast4",
    "apiTransKey",
    "apiLogin",
    ...userPiiRedactList
];
/**
 * Add body to request object
 * @see https://github.com/pinojs/pino-http#logging-request-body
 */
const requestBodySerializer = (req) => {
    req.body = req.raw.body;
    return req;
};
/**
 * Add body to response object
 * @see https://github.com/pinojs/pino-http#logging-request-body
 */
const responseBodySerializer = (res) => {
    res.body = res.raw.logger_body;
    return res;
};
/**
 * Logger Class
 * Allows the ability to expose custom logging
 * and reporting to external services
 * Utilizes winston for logging to stdout
 * Utilizes sentry to report to remote logger servers
 */
class Logger {
    constructor(configToUse = config_1.default) {
        /**
         * A list of user PII properties.
         */
        this.defaultUserPiiProps = defaultUserPiiProps;
        this.userPiiRedactList = userPiiRedactList;
        /**
         * This function builds a redact list from a list of user PII paths and a list
         * of user PII property names. It joins each property to each path with a '.'.
         * The output of this function is intended to be used to configure the redact
         * property of an instance of @greenlight/logger.
         */
        this.buildUserPiiRedactList = buildUserPiiRedactList;
        /**
         * Allows for changing the logger config and adding remote logger
         */
        this.setConfig = (configToUse = config_1.default) => {
            // Used so that we can reference it by the Logger singleton
            this.config = merge_1.default(configToUse, {
                redact: union_1.default(redact, configToUse.redact),
                messageKey: "message" // Required for logging to New Relic, the Pino default is "msg" which does not play nicely
            });
            // Used so that we can reference it inside of express middlewares
            // e.g when blackListEndpoints middleware gets called, this doesn't reference this object
            config = this.config;
            this.logger = pino_1.default(this.config);
            let serializers = {};
            if (configToUse.logRequestBody) {
                serializers = {
                    req: requestBodySerializer,
                    res: responseBodySerializer
                };
            }
            this.pinoExpress = express_pino_logger_1.default({
                logger: this.logger,
                serializers
            });
        };
        this.blackListEndpoints = (req, res, next) => {
            if (!req.log || !res.log) {
                throw new Error("Make sure to add pinoExpress middleware to express prior to blackListEndpoints middleware");
            }
            if (Array.isArray(config.blackListEndpoints)) {
                config.blackListEndpoints.forEach(endpoint => {
                    if (req.url === endpoint || req.originalUrl === endpoint) {
                        // @ts-expect-error
                        req.log = {
                            trace: () => { },
                            debug: () => { },
                            info: () => { },
                            warn: () => { },
                            error: () => { },
                            fatal: () => { }
                        };
                        // @ts-expect-error
                        res.log = {
                            trace: () => { },
                            debug: () => { },
                            info: () => { },
                            warn: () => { },
                            error: () => { },
                            fatal: () => { }
                        };
                    }
                });
            }
            next();
        };
        /**
         * Adds an interceptor on the response send function used to
         * grab response data for request logging
         */
        this.logRequest = (app) => {
            const originalSend = app.response.send;
            // @ts-expect-error
            // eslint-disable-next-line no-param-reassign
            app.response.send = function sendOverWrite(body) {
                originalSend.call(this, body);
                // @ts-ignore
                this.logger_body = body;
            };
            app.use(this.pinoExpress);
        };
        /**
         * Allows the ability to add a remote logger to the logger instance
         */
        this.setRemoteLogger = (remoteLogger) => {
            this.remoteLogger = remoteLogger;
        };
        this.wrapErrorInstance = (arg) => {
            if (Array.isArray(arg)) {
                return arg.map(this.wrapErrorInstance);
            }
            if (arg instanceof Error) {
                return arg.stack;
            }
            return arg;
        };
        this.info = (message, mergingObjects, ...extras) => {
            this.sendLog("info", message, mergingObjects, extras);
        };
        this.warn = (message, mergingObjects, ...extras) => {
            this.sendLog("warn", message, mergingObjects, extras);
        };
        this.debug = (message, mergingObjects, ...extras) => {
            this.sendLog("debug", message, mergingObjects, extras);
        };
        this.error = (message, mergingObjects, ...extras) => {
            this.sendLog("error", message, mergingObjects, extras);
        };
        this.setConfig(configToUse);
    }
    sendLog(level, arg1, arg2, arg3) {
        const message = this.wrapErrorInstance(arg1);
        const mergingObjects = this.wrapErrorInstance(arg2);
        const extras = this.wrapErrorInstance(arg3);
        // pino will merge the first argument directly into the log line if it is
        // an object, but @greenlight/logger typically receives objects as the
        // second argument, so we need to conditionally switch the order of
        // the arguments if the second argument is provided.
        if (arg1 && !arg2 && !arg3.length) {
            this.logger[level](message);
        }
        else if (arg1 && arg2 && !arg3.length) {
            this.logger[level](mergingObjects, message);
        }
        else if (arg1 && arg2 && arg3.length) {
            this.logger[level](mergingObjects, message, extras);
        }
        else {
            throw new Error("Please provide at least one argument for logger");
        }
    }
}
// Create singleton for shared usage
const logger = new Logger();
exports.default = logger;
module.exports = logger;
//# sourceMappingURL=logger.js.map